import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useLogout } from '@/features/auth/queries'
import { LanguagePicker } from '@/features/language/LanguagePicker'
import { LoginMethodSection } from '@/features/login-method/LoginMethodSection'
import { startIdentityLink } from '@/features/login-method/api'
import {
  readLinkOutcome,
  withoutLinkOutcome,
  type LinkOutcome,
} from '@/features/login-method/outcome'
import { useLoginMethods } from '@/features/login-method/queries'
import type { Messages } from '@/features/language/messages'
import { useLanguage, useMessages } from '@/features/language/use-language'
import { LANGUAGE_NAMES } from '@/features/language/types'
import { NicknameSheet } from '@/features/nickname/NicknameSheet'
import { useNickname } from '@/features/nickname/queries'
import { useTheme } from '@/features/theme/use-theme'
import { THEME_IDS } from '@/features/theme/types'
import { Chip } from '@/shared/ui/Chip'
import type { ProviderId } from '@/shared/ui/providers'
import { DisclosureRow } from '@/shared/ui/DisclosureRow'
import { IconButton } from '@/shared/ui/IconButton'
import { Notice } from '@/shared/ui/Notice'
import { OutlineButton } from '@/shared/ui/OutlineButton'
import { ArrowLeftIcon } from '@/shared/ui/icons'

/**
 * 설정 화면.
 *
 * 언어가 첫 항목이고 분위기가 그다음이다. 언어를 잘못 골라 화면을 읽지 못하게 된 사람이
 * 되돌아올 길이 이 항목뿐이라 설정에 들어서자마자 스크롤 없이 보여야 한다.
 *
 * 돌아가는 자리는 왼쪽 화살표다. 설정은 자기 주소를 갖고 이력에 자리가 남는 화면이라 층을
 * 걷는 가위표를 쓰지 않는다. 홈 화면에서 연 앱에는 주소창이 없어 이 자리가 화면 안의 유일한
 * 길이다.
 *
 * 행은 두 성격으로 갈린다. 다른 자리를 여는 행은 쉐브론을 갖고, 그 자리에서 일을 끝내는 행은
 * 갈 곳이 없으므로 쉐브론 대신 테두리 버튼으로 선다.
 *
 * 안내는 일이 일어난 구역 바로 위에 붙는다. 화면 맨 위 한 자리로 올리면 로그아웃 실패 안내가
 * 언어에 대한 말처럼 읽힌다.
 */
export function SettingsPage() {
  const m = useMessages()
  const {
    languageId,
    chosenLanguage,
    chooseLanguage,
    saveFailed: languageSaveFailed,
  } = useLanguage()
  const { themeId, setTheme } = useTheme()
  const { mutate: signOut, isPending: signingOut, isError: signOutFailed } = useLogout()
  const [picking, setPicking] = useState(false)
  const [editingName, setEditingName] = useState(false)
  const [leavingProvider, setLeavingProvider] = useState<ProviderId | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  /*
   * 돌아온 주소가 나른 결과는 첫 렌더에서 한 번 읽어 두고 주소에서는 곧바로 지운다. 남겨 두면
   * 새로고침과 뒤로가기에서 같은 안내가 다시 서고, 이미 추가한 항목 위에 추가했다는 줄이 또 붙는다.
   */
  const [linkOutcome] = useState(() => readLinkOutcome(searchParams))
  const languageRow = useRef<HTMLButtonElement>(null)
  const nicknameRow = useRef<HTMLButtonElement>(null)

  /* 이 값을 그리는 화면이 여기 하나라 설정이 자기 요청으로 읽는다. */
  const nickname = useNickname(!signingOut)
  const loginMethods = useLoginMethods(!signingOut)

  useEffect(() => {
    const next = withoutLinkOutcome(searchParams)
    if (next) setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  /** 고르고 나면 누른 자리로 포커스를 되돌린다. 자리를 잃지 않게 한다. */
  const closePicker = () => {
    setPicking(false)
    languageRow.current?.focus()
  }

  const closeNameSheet = () => {
    setEditingName(false)
    nicknameRow.current?.focus()
  }

  return (
    <div className="mx-auto flex max-w-[560px] flex-col">
      {/* 로그아웃이 도는 동안 돌아가는 자리도 함께 잠근다. 그 사이에 떠나면 끊기가 끝났을 때
          어디에 서 있어야 하는지 어긋난다. */}
      <div className="-ml-2.5 flex items-center gap-1">
        {signingOut ? (
          <IconButton label={m.settings.screen.back} disabled>
            <ArrowLeftIcon />
          </IconButton>
        ) : (
          <IconButton label={m.settings.screen.back} to="/">
            <ArrowLeftIcon />
          </IconButton>
        )}
        <h1 className="font-serif text-2xl tracking-tight">{m.settings.screen.title}</h1>
      </div>

      <div className="mt-8 flex flex-col gap-7">
        {languageSaveFailed && <Notice>{m.settings.language.saveFailed}</Notice>}

        {/* 사람이 먼저 서고 공간의 설정이 뒤에 선다. 우리가 지어 준 닉네임이라 사용자는 아직
            그것을 모르므로 목록 아래쪽에 두면 발견되지 않은 채 남는다. */}
        {nickname.isError && <Notice>{m.settings.nickname.loadFailed}</Notice>}
        <DisclosureRow
          ref={nicknameRow}
          label={m.settings.nickname.label}
          value={nickname.data?.nickname ?? ''}
          onClick={() => setEditingName(true)}
          disabled={signingOut || !nickname.data}
        />

        <DisclosureRow
          ref={languageRow}
          label={m.settings.language.label}
          value={<span lang={languageId}>{LANGUAGE_NAMES[languageId]}</span>}
          onClick={() => setPicking(true)}
          disabled={signingOut}
        />

        <section className="flex flex-col gap-3">
          <h2 className="text-ink-muted text-sm">{m.settings.theme.label}</h2>
          <div className="flex gap-2">
            {THEME_IDS.map((id) => (
              <Chip
                key={id}
                selected={themeId === id}
                onClick={() => setTheme(id)}
                disabled={signingOut}
              >
                {m.settings.theme[id]}
              </Chip>
            ))}
          </div>
        </section>

        <LoginMethodSection
          methods={loginMethods.data?.loginMethods ?? []}
          loadFailed={loginMethods.isError}
          notice={linkNotice(m, linkOutcome)}
          leaving={leavingProvider}
          onAdd={(provider) => {
            setLeavingProvider(provider)
            startIdentityLink(provider, 'settings')
          }}
          disabled={signingOut}
        />

        <section className="flex flex-col">
          <DisclosureRow label={m.settings.legal.terms} to="/terms" />
          <DisclosureRow label={m.settings.legal.privacy} to="/privacy" />
        </section>

        {/* 여백으로 떨어뜨린다. 목록 맨 아래이고 손이 자주 가는 자리에서 멀다. */}
        <section className="mt-10 flex flex-col gap-3">
          {signOutFailed && <Notice>{m.auth.logout.failed}</Notice>}
          <OutlineButton block onClick={() => signOut()} disabled={signingOut}>
            {signingOut ? m.auth.logout.pending : m.auth.logout.label}
          </OutlineButton>
        </section>
      </div>

      {editingName && nickname.data && (
        <NicknameSheet
          current={nickname.data.nickname}
          onSaved={closeNameSheet}
          onClose={closeNameSheet}
        />
      )}

      {picking && (
        <LanguagePicker
          effective={languageId}
          chosen={chosenLanguage}
          onChoose={(next) => {
            chooseLanguage(next)
            closePicker()
          }}
          onClose={closePicker}
        />
      )}
    </div>
  )
}

/** 돌아온 주소가 나르는 결과. 화면은 이 값만 보고 안내를 고른다. */
/**
 * 취소하고 돌아온 경우에는 안내를 붙이지 않는다. 취소는 실패가 아니라 사용자가 방금 고른 것이고
 * 그것을 다시 알리면 하지 않기로 한 선택을 되묻는 것이 된다. 목록의 그 항목이 추가 버튼을 그대로
 * 달고 서 있어 화면 자체가 결과를 말한다.
 *
 * 모르는 값이 오거나 이름이 필요한 안내에 provider가 빠져 있으면 실패 하나로 닫는다. 값이
 * 무엇이든 사용자가 할 일은 한 번 더 누르는 것 하나다.
 */
function linkNotice(m: Messages, outcome: LinkOutcome): string | null {
  if (!outcome) return null
  if (outcome.kind === 'failed') return m.settings.loginMethod.failed
  const name = m.auth.provider[outcome.provider]
  return outcome.kind === 'added'
    ? m.settings.loginMethod.addedNotice(name)
    : m.settings.loginMethod.alreadyLinked(name)
}
